import React, { useState } from 'react'
import styled from 'styled-components'
import moment, { Moment } from 'moment'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import CircularProgress from '@material-ui/core/CircularProgress'
import { apiShiftDuplicate } from '../../../../apis/shift-duplicate'
import MultiDatePicker from './MultiDatePicker'
import { useCrewCalender } from '../StoreCrewCalender'
import { Shift } from '@src/types/Shift'
import { useAppStore } from '@src/AppStore'
import { useSnackbar } from '@src/components/GlobalSnackbar'

const DuplicateDialog = ({
  shift,
  open,
  onClose,
}: {
  shift: Shift,
  open: boolean,
  onClose(): void,
}) => {
  const { serialId } = useAppStore()
  const [date, setDate] = useState<Moment>(moment())
  const [dates, setDates] = useState<Moment[]>([])
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const { goRefresh } = useCrewCalender()
  const { alert } = useSnackbar()
  if (!open) return null
  const handleDuplicate = () => {
    if (!dates.length) {
      alert({ type: 'error', message: 'Select at least one date to duplicate to' })
      return
    }
    setIsSaving(true)
    apiShiftDuplicate({
      shiftId: shift.id,
      dates: dates.map((date: Moment) => date.toISOString()),
      serialId,
    }).then((response: any) => {
      const newShifts: any[] = (response && response.data && response.data.shifts) || []
      if (newShifts.length) {
        const formattedDates: string[] = dates.map((date: Moment) => date.format('DD-MM-YY'))
        const message: string = `Shift ${shift.id} was duplicated to ${formattedDates.join(', ')}`
        // Deliberately not pushing the response into the calendar store. This
        // endpoint returns ShiftDto, which has no `products` field, while the
        // calendar renders shifts from /v3/shifts and DRSItem reads
        // data.products.spraySeal unguarded. goRefresh() refetches in the right
        // shape and fetchDrss() normalises products.
        alert({ type: 'success', message })
        goRefresh()
        onClose()
      } else {
        alert({
          type: 'error',
          message: `Shift ${shift.id} was not duplicated. Refresh the calendar before trying again.`,
          autoHide: false,
        })
      }
    }).catch((err: any) => {
      console.log(`DuplicateDialog handleDuplicate() failed`, err)
      const status = err && err.response && err.response.status
      const message: string = status === 504 || status === 502
        ? `Duplicating shift ${shift.id} is taking longer than expected. Refresh the calendar to check whether it was created before trying again.`
        : `Failed to duplicate shift ${shift.id}. Refresh the calendar to check whether it was created before trying again.`
      alert({ type: 'error', message, autoHide: false })
    }).finally(() => {
      setIsSaving(false)
    })
  }
  return <Dialog open={open} onClose={onClose} aria-labelledby='form-dialog-title'>
    <DialogTitle id='form-dialog-title'>{`Duplicate Shift on ${moment(shift.date).format('DD/MM/YYYY')}`}</DialogTitle>
    <DialogContent>
      <MultiDatePicker
        variant='static'
        value={date}
        selectedItems={dates}
        onChange={date => {
          const index = dates.findIndex(item => {
            return moment(date).isSame(moment(item), 'date')
          })
          setDate(moment(date))
          if (index !== -1) {
            setDates([...dates.slice(0, index), ...dates.slice(index + 1)])
          } else {
            setDates([...dates, date])
          }
        }}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button onClick={handleDuplicate} color='primary' disabled={isSaving}>
        {isSaving && <React.Fragment><Spinner size={16} /> {`Duplicating`} </React.Fragment>}
        {!isSaving && 'Duplicate '}
      </Button>
    </DialogActions>
  </Dialog>
}

const Spinner = styled(CircularProgress)`
  margin-right: 10px;
  position: relative;
`

export default DuplicateDialog